import { useMsal } from "@azure/msal-react";
import {
  PrimaryButton,
  Stack,
  TextField,
} from "@fluentui/react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GlobalVars, listname, NewEntrySite } from "../../constants/global-values";
import { common } from "../../messages/common-items";
import { getListItems, siteEntry_List } from "../../services/graph-service";
import {
  getTokenAndListInfo,
  phoneNumFormat,
  validatePhone,
} from "../../utils/item-helper";
import { useDispatch } from "react-redux";
import "../../assets/styles/site-login.css";
import { setGlobalData } from "../../store/global-slice";
import { siteentrylist } from "../../messages/site-entry";

// ---------------- Types ----------------
interface UserItem {
  id: string;
  WorkerName: string;
  PhoneNumber: string;
}

interface EntryItem {
  WorkerIdLookupId?: number;
  EntryTrainingCompleted?: boolean;
}

// ---------------- Component ----------------
export default function PhoneLoginPage() {
  // Local component state
  const [phoneNumber, setPhoneNumber] = useState("");  // Formatted phone number input
  const [error, setError] = useState("");              // Error message to display
  const [isLoginFailed, setIsLoginFailed] = useState(false); // Tracks login failure for retry button
  const [loading, setLoading] = useState(false);       // Loading spinner state

  // Extract messages from localization
  const { Labels, ErrorMessages, SuccessMessages } = siteentrylist;
  const { ErrorMessages: Err_Common } = common;


  // Hooks for Redux, routing, MSAL
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { instance } = useMsal();

  /** Detect login source from the current URL path */
  const loginSource = location.pathname.split("/")[1];
  const isNewEntryFlow = ["shinki", "nyuujou"].includes(loginSource);

  /** Handle phone number input and auto-format */
  const handlePhoneNumberChange = (
    event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    // Keep only digits, limit to 11, then format (xxx-xxxx-xxxx)
    const digits = event.currentTarget.value.replace(/\D/g, "").slice(0, 11);
    setPhoneNumber(phoneNumFormat(digits));

    // Reset error state when user edits input
    setIsLoginFailed(false);
    setError("");
  };

  /** Save user info in Redux + global variables */
  const saveUserData = (user: UserItem) => {
    // WARNING: GlobalVars mutation → consider replacing with Redux-only in future
    GlobalVars.userid = user.id;
    GlobalVars.phoneNumber_enter = user.PhoneNumber;

    dispatch(
      setGlobalData({
        key: "phoneUser",
        value: {
          userId: user.id,
          userName: user.WorkerName,
          phoneNumber: user.PhoneNumber,
        },
      })
    );
  };

  /** Fetch existing site entry for user (if any) */
  const checkSiteEntry = async (userId: string): Promise<EntryItem | undefined> => {
    const { token, siteId, listId } = await getTokenAndListInfo(instance, NewEntrySite);
    const siteEntries = await siteEntry_List(token, siteId, listId);
    return siteEntries.find((item: EntryItem) => item.WorkerIdLookupId?.toString() === userId);
  };

  /** Navigation logic after login success */
  const navigateToNextStep = (entry?: EntryItem) => {
    if (isNewEntryFlow) {
      if (entry) {
        // If entry exists → check training status
        entry.EntryTrainingCompleted
          ? navigate("/nyuujou/seikou", { state: { from: loginSource } })
          : navigate("/shinki/kyouiku", { state: { from: loginSource } });
      } else {
        // No entry found → direct to error/registration
        navigate(
          loginSource === "shinki" ? "/shinki/touroku" : "/nyuujou/shinkierror",
          { state: { from: loginSource } }
        );
      }
    } else {
      // Default flow → go to personal management page
      navigate(`/shokai/kanri/${GlobalVars.userid}`, { state: { from: "sitelogin" } });
    }
  };

  /** Validate input before login */
  const validateInput = (rawPhone: string): boolean => {
    if (!rawPhone) {
      setError(Err_Common.PHONE_REQUIRED_FIELD);
      return false;
    }
    if (!validatePhone(rawPhone)) {
      setError(Err_Common.INVALID_PHONE_ERR);
      return false;
    }
    return true;
  };

  /** Handle the entire login process */
  const handleLogin = async () => {
    setError("");
    setIsLoginFailed(false);

    // Remove dashes before validation and lookup
    const rawPhone = phoneNumber.replace(/-/g, "");
    if (!validateInput(rawPhone)) return;

    try {
      setLoading(true);

      // Step 1: Fetch all registered users
      const { token, siteId, listId } = await getTokenAndListInfo(instance, listname);
      const items: UserItem[] = await getListItems(token, siteId, listId);

      // Step 2: Match user by phone number
      const match = items.find((item) => item.PhoneNumber?.toString() === rawPhone);
      GlobalVars.phoneNumber_enter = rawPhone;
      if (!match) {
        // If no match → decide by flow
        if (isNewEntryFlow) {
          setIsLoginFailed(true); // Show retry button
        } else {
          navigate("/shokai/kojinerror", { state: { from: "sitelogin" } });
        }
        return;
      }

      // Step 3: Save user in Redux/global
      saveUserData(match);

      // Step 4: Check site entry if needed
      const entry = isNewEntryFlow ? await checkSiteEntry(match.id) : undefined;

      // Step 5: Navigate to correct page
      navigateToNextStep(entry);
    } catch (err) {
      console.error("Login failed", err);
      setError(Err_Common.LOGIN_ERR);
    } finally {
      setLoading(false);
    }
  };

  // ---------------- Render ----------------
  return (
    <div className="login-background">
      <div className="login-card">
        <div className="login-icon">
          <img src="/images/phone.png" alt="Phone Icon" className="login-img" />
        </div>
        <h2 className="login-title">{Labels.LOGIN_TITLE}</h2>
        <p className="login-subtitle">{Labels.LOGIN_SUBTITLE}</p>
        <p className="login-label">{Labels.LOGIN_LABEL}</p>
        <TextField
          className="phonenumberbutton"
          placeholder={SuccessMessages.PHONE_VALID}
          type="tel"
          value={phoneNumber}
          onChange={handlePhoneNumberChange}
          maxLength={13}
          autoComplete="off"
          errorMessage={error}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleLogin();
            }
          }}
        />
        <PrimaryButton
          className={isNewEntryFlow ? "shinkilogin-button" : "login-button"}
          text={Labels.LOGIN_BUTTON}
          onClick={handleLogin}
          disabled={loading}
        />

        {/* Retry button on failed login (new entry flow only) */}
        {isLoginFailed && isNewEntryFlow && (
          <Stack>
            <PrimaryButton
              className="failedlogin"
              text={ErrorMessages.FAILED_LOGIN}
              onClick={handleLogin}
            />
          </Stack>
        )}

        {/* Disclaimer text */}
        <p className="login-disclaimer">{Labels.LOGIN_DISCLAIMER}</p>
      </div>
    </div>
  );
}
